'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { ArrowRight, Heart, Users, Share2, Flame, MessageCircle, Images, Search, MapPin, Building2, GraduationCap, Briefcase, Calendar, BookOpen, LogIn, Loader2, Bell } from 'lucide-react';
import { mockCompany, mockInstitutions } from '@/lib/mock-data';
import { formatLifeSpan, formatDate } from '@/lib/utils';
import { Institution } from '@/lib/types';

interface Memorial {
  id: string;
  slug: string;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  city: string | null;
  placeOfResidence: string | null;
  imagePath: string | null;
  institutions?: Array<{
    id: string;
    institution: {
      id: string;
      name: string;
      type: string;
    };
  }>;
}

export default function Home() {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [memorials, setMemorials] = useState<Memorial[]>([]);
  const [loadingMemorials, setLoadingMemorials] = useState(true);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    router.push(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleDemoLogin = async () => {
    setIsLoggingIn(true);
    try {
      const res = await fetch('/api/auth/demo-login', { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        router.push(data.redirectUrl);
        router.refresh();
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Fetch memorials from database
  useEffect(() => {
    const fetchMemorials = async () => {
      try {
        const res = await fetch('/api/memorials');
        if (res.ok) {
          const data = await res.json();
          setMemorials(data);
        }
      } catch (err) {
        console.error('Error fetching memorials:', err);
      } finally {
        setLoadingMemorials(false);
      }
    };
    fetchMemorials();
  }, []);

  // Get recent memorials (sorted by death date, most recent first)
  const recentMemorials = [...memorials]
    .sort((a, b) => {
      const dateA = a.dateOfDeath ? new Date(a.dateOfDeath).getTime() : 0;
      const dateB = b.dateOfDeath ? new Date(b.dateOfDeath).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, 6);

  const getInstitutionIcon = (type: string) => {
    const normalizedType = type.toLowerCase();
    switch (normalizedType) {
      case 'school':
      case 'university':
        return <GraduationCap className="w-5 h-5" />;
      case 'company':
        return <Briefcase className="w-5 h-5" />;
      case 'club':
        return <Users className="w-5 h-5" />;
      default:
        return <Building2 className="w-5 h-5" />;
    }
  };

  return (
    <main className="min-h-screen bg-memorial-background">
      {/* Navigation */}
      <nav className="bg-white border-b border-memorial-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="font-serif text-xl text-memorial-primary">
              Gedenkportal
            </Link>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link
                href="/search"
                className="text-memorial-text-secondary hover:text-memorial-primary transition-colors flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Suchen</span>
              </Link>
              <Link
                href="/search"
                className="hidden md:flex text-memorial-text-secondary hover:text-memorial-primary transition-colors items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                <span>Alle Gedenkseiten</span>
              </Link>
              <Link
                href="/subscribe"
                className="hidden md:flex text-memorial-text-secondary hover:text-memorial-primary transition-colors items-center gap-2"
              >
                <Bell className="w-4 h-4" />
                <span>Benachrichtigungen</span>
              </Link>
              <button
                onClick={handleDemoLogin}
                disabled={isLoggingIn}
                className="flex items-center gap-2 px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">{isLoggingIn ? 'Lädt...' : 'Familien-Login'}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Search */}
      <section className="relative bg-gradient-to-br from-memorial-primary via-memorial-primary to-memorial-primary/90 text-white py-16 md:py-24 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4">
            Digitale Gedenkseiten
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto mb-8">
            Moderne, würdevolle Online-Gedenkseiten für einen respektvollen Abschied
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name suchen..."
                className="w-full pl-14 pr-32 py-4 rounded-full text-memorial-text-primary bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-memorial-accent"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-memorial-primary text-white rounded-full font-medium hover:bg-memorial-primary/90 transition-colors"
              >
                Suchen
              </button>
            </div>
          </form>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/search"
              className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              <span>Alle Gedenkseiten durchsuchen</span>
            </Link>
            <Link
              href="/subscribe"
              className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
            >
              <Bell className="w-4 h-4" />
              <span>Benachrichtigungen erhalten</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Recent Memorials Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary">
                Aktuelle Gedenkseiten
              </h2>
              <p className="text-memorial-text-secondary mt-1">
                Die neuesten veröffentlichten Gedenkseiten
              </p>
            </div>
            <Link
              href="/search"
              className="hidden sm:flex items-center gap-2 text-memorial-primary hover:text-memorial-secondary transition-colors font-medium"
            >
              <span>Alle ansehen</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loadingMemorials ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
            </div>
          ) : recentMemorials.length === 0 ? (
            <div className="text-center py-12 text-memorial-text-secondary">
              Keine Gedenkseiten gefunden
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentMemorials.map((memorial) => {
              const fullName = `${memorial.firstName || ''} ${memorial.lastName || ''}`.trim();
              const lifeSpan = formatLifeSpan(memorial.dateOfBirth, memorial.dateOfDeath);

              return (
                <Link
                  key={memorial.id}
                  href={`/m/${memorial.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-memorial-border hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative h-52 bg-memorial-muted">
                    {memorial.imagePath ? (
                      <Image
                        src={memorial.imagePath}
                        alt={fullName}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-memorial-primary/10">
                        <span className="text-5xl font-serif text-memorial-primary/50">
                          {memorial.firstName?.charAt(0)}{memorial.lastName?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="font-serif text-xl">{fullName}</h3>
                      <p className="text-white/80 text-sm">{lifeSpan}</p>
                    </div>
                  </div>
                  <div className="p-4">
                    {/* Location */}
                    {memorial.city && (
                      <div className="flex items-center gap-2 text-sm text-memorial-text-secondary mb-2">
                        <MapPin className="w-4 h-4" />
                        <span>{memorial.placeOfResidence || memorial.city}</span>
                      </div>
                    )}
                    {/* Death date */}
                    {memorial.dateOfDeath && (
                      <div className="flex items-center gap-2 text-sm text-memorial-text-secondary mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>Verstorben am {formatDate(memorial.dateOfDeath)}</span>
                      </div>
                    )}
                    {/* Institution badges */}
                    {memorial.institutions && memorial.institutions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-memorial-border">
                        {memorial.institutions.slice(0, 2).map((ic) => (
                          <span
                            key={ic.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-memorial-muted rounded-full text-xs text-memorial-text-secondary"
                          >
                            {getInstitutionIcon(ic.institution.type)}
                            <span className="truncate max-w-[100px]">{ic.institution.name}</span>
                          </span>
                        ))}
                        {memorial.institutions.length > 2 && (
                          <span className="px-2 py-1 bg-memorial-muted rounded-full text-xs text-memorial-text-secondary">
                            +{memorial.institutions.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Link
              href="/search"
              className="inline-flex items-center gap-2 px-6 py-3 bg-memorial-primary text-white rounded-full font-medium hover:bg-memorial-primary/90 transition-colors"
            >
              <span>Alle Gedenkseiten ansehen</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Search by Institution */}
      <section className="py-12 bg-memorial-muted/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary mb-2">
              Nach Institution suchen
            </h2>
            <p className="text-memorial-text-secondary">
              Finden Sie Gedenkseiten ehemaliger Mitglieder, Schüler oder Kollegen
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {mockInstitutions.map((inst) => (
              <Link
                key={inst.id}
                href={`/search?institution=${inst.id}`}
                className="flex items-center gap-4 p-5 bg-white rounded-xl border border-memorial-border hover:shadow-md transition-all group"
              >
                <div className="w-14 h-14 rounded-full bg-memorial-muted flex items-center justify-center flex-shrink-0 group-hover:bg-memorial-primary/10 transition-colors">
                  {getInstitutionIcon(inst.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-memorial-text-primary group-hover:text-memorial-primary transition-colors truncate">
                    {inst.name}
                  </h3>
                  <p className="text-sm text-memorial-text-secondary">
                    {inst.city} • {inst.memorialCount} Gedenkseiten
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-memorial-text-secondary group-hover:text-memorial-primary transition-colors flex-shrink-0" />
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              href="/search"
              className="inline-flex items-center gap-2 text-memorial-primary hover:text-memorial-secondary transition-colors font-medium"
            >
              <span>Alle Institutionen durchsuchen</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary text-center mb-4">
            Funktionen unserer Gedenkseiten
          </h2>
          <p className="text-memorial-text-secondary text-center max-w-2xl mx-auto mb-12">
            Moderne Werkzeuge für würdevolles Gedenken
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-memorial-muted">
              <div className="w-12 h-12 rounded-full bg-memorial-primary/10 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-memorial-primary" />
              </div>
              <h3 className="font-serif text-xl text-memorial-primary mb-2">Persönliche Gedenkseite</h3>
              <p className="text-memorial-text-secondary">
                Würdevolle Präsentation mit Foto, Lebensdaten und Nachruf
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-memorial-muted">
              <div className="w-12 h-12 rounded-full bg-memorial-secondary/10 flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6 text-memorial-secondary" />
              </div>
              <h3 className="font-serif text-xl text-memorial-primary mb-2">Kondolenzbuch</h3>
              <p className="text-memorial-text-secondary">
                Digitales Kondolenzbuch mit Familienantworten
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-memorial-muted">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                <Flame className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-serif text-xl text-memorial-primary mb-2">Kerzenwand</h3>
              <p className="text-memorial-text-secondary">
                Virtuelle Kerzen zum Gedenken anzünden
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-memorial-muted">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Images className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-serif text-xl text-memorial-primary mb-2">Bildergalerie</h3>
              <p className="text-memorial-text-secondary">
                Erinnerungsfotos in einer ansprechenden Galerie
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-memorial-muted">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-serif text-xl text-memorial-primary mb-2">Institutionen</h3>
              <p className="text-memorial-text-secondary">
                Verknüpfung mit Schulen, Vereinen und Arbeitgebern
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-memorial-muted">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-serif text-xl text-memorial-primary mb-2">Einfaches Teilen</h3>
              <p className="text-memorial-text-secondary">
                Per WhatsApp, E-Mail oder Link mit Familie und Freunden teilen
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-memorial-primary text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">
            Interesse an unserem Gedenkportal?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Kontaktieren Sie uns für eine Demo oder weitere Informationen zu unserem Gedenkportal für Bestattungsinstitute.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {mockCompany.websiteUrl && (
              <a
                href={mockCompany.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-memorial-primary rounded-full font-medium hover:bg-white/90 transition-colors"
              >
                <span>Mehr erfahren</span>
                <ArrowRight className="w-5 h-5" />
              </a>
            )}
            <Link
              href="/search"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
            >
              <Search className="w-5 h-5" />
              <span>Gedenkseiten durchsuchen</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-memorial-primary/95 text-white/60">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="font-serif text-white text-lg mb-1">Gedenkportal</p>
              <p className="text-sm">Demo-Version | Ein Service von {mockCompany.name}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link href="/search" className="text-white/80 hover:text-white transition-colors text-sm">
                Suchen
              </Link>
              <Link href="/subscribe" className="text-white/80 hover:text-white transition-colors text-sm">
                Benachrichtigungen
              </Link>
              <Link href="/m/hans-mueller-1942-2024" className="text-white/80 hover:text-white transition-colors text-sm">
                Demo Gedenkseite
              </Link>
              <button
                onClick={async () => {
                  const res = await fetch('/api/auth/demo-login', { method: 'POST' })
                  const data = await res.json()
                  if (res.ok) {
                    router.push(data.redirectUrl)
                    router.refresh()
                  }
                }}
                className="text-white/80 hover:text-white transition-colors text-sm"
              >
                Familien-Dashboard (Demo)
              </button>
              <Link href="/admin" className="text-white/80 hover:text-white transition-colors text-sm">
                Admin-Bereich
              </Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 text-center text-sm">
            <p>Kompatibel mit Consul-DBA</p>
          </div>
        </div>
      </footer>
    </main>
  );
}
